import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useState } from "react";
import {
  createSecureMessageListener,
  sendSecurePostMessage,
} from "../../../shared/utils/postmessage-validation";
import {
  type TransactionRequest,
  type TransactionResponse,
  type WalletAccount,
  WalletAdapterStatus,
} from "../utils/types";
import { WalletContext, type WalletContextType } from "./shared-wallet-context";

interface ExternalWalletProviderProps {
  children: ReactNode;
}

interface WalletStateMessage {
  type: "walletState";
  data: {
    isConnected: boolean;
    address?: string;
    chainId?: number;
    walletInfo?: string;
  };
}

interface SendTransactionResponseMessage {
  type: "sendTransactionResponse";
  success: boolean;
  data?: TransactionResponse;
  error?: string;
}

type WalletMessage = WalletStateMessage | SendTransactionResponseMessage;

export function ExternalWalletProvider({ children }: ExternalWalletProviderProps) {
  const { t } = useLingui();
  const [status, setStatus] = useState<WalletAdapterStatus>(WalletAdapterStatus.DISCONNECTED);
  const [selectedAccount, setSelectedAccount] = useState<WalletAccount | null>(null);
  const [pendingTransaction, setPendingTransaction] = useState<{
    resolve: (value: TransactionResponse) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Handle messages from parent with secure validation
  useEffect(() => {
    const handleMessage = createSecureMessageListener(
      (data: WalletMessage) => {
        if (data.type === "walletState") {
          const walletState = data.data;

          setStatus(
            walletState.isConnected
              ? WalletAdapterStatus.CONNECTED
              : WalletAdapterStatus.DISCONNECTED,
          );

          setSelectedAccount(
            walletState.isConnected && walletState.address
              ? {
                  address: walletState.address,
                  chainId: walletState.chainId || 1,
                  name: walletState.walletInfo || t`External Wallet`,
                }
              : null,
          );
        } else if (data.type === "sendTransactionResponse") {
          const { success, data: responseData, error } = data;

          if (pendingTransaction) {
            if (success) {
              pendingTransaction.resolve(responseData as TransactionResponse);
            } else {
              pendingTransaction.reject(new Error(error || t`Transaction sending failed`));
            }
            setPendingTransaction(null);
          }
        }
      },
      {
        expectedTypes: ["walletState", "sendTransactionResponse"],
      },
    );

    window.addEventListener("message", handleMessage);

    // Request initial wallet state using secure postMessage
    sendSecurePostMessage(window.parent, { type: "getWalletState" });

    return () => window.removeEventListener("message", handleMessage);
  }, [pendingTransaction, t]);

  const connect = async (): Promise<void> => {
    sendSecurePostMessage(window.parent, { type: "connectWallet" });
  };

  const disconnect = async (): Promise<void> => {
    sendSecurePostMessage(window.parent, { type: "disconnectWallet" });
  };

  const sendTransaction = async (tx: TransactionRequest): Promise<TransactionResponse> => {
    return new Promise((resolve, reject) => {
      // Store the promise handlers
      setPendingTransaction({ resolve, reject });

      // Send the transaction request using secure postMessage
      sendSecurePostMessage(window.parent, {
        type: "sendTransaction",
        data: tx,
      });
    });
  };

  const contextValue: WalletContextType = {
    status,
    selectedAccount,
    connect,
    disconnect,
    sendTransaction,
  };

  return <WalletContext.Provider value={contextValue}>{children}</WalletContext.Provider>;
}
